// client/src/lib/firebase.ts
// Firebase client SDK initialisation for AGOC School.
//
// IMPORTANT: This app authenticates against the AGOC Platform Firebase project
// (agoc-platform-dev / agoc-platform-prod), NOT the old icaros-agoc-school project.
// All env vars must reference the agoc-platform-dev credentials from the Firebase console.
//
// The Firebase SDK is used exclusively for:
//   1. Authentication (email/password + Google OAuth)
//   2. Obtaining ID tokens to attach as Bearer tokens on API calls
//
// Firestore is NOT called directly from the client — all data operations go through
// the agoc-school server or the platform backend API.
// Firebase Storage calls will be added here as needed (e.g. study material uploads).

import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  browserLocalPersistence,
  setPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  UserCredential,
} from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, FirebaseStorage } from 'firebase/storage';

// ── Firebase configuration ─────────────────────────────────────────────────
// Sourced from VITE_FIREBASE_* environment variables (see .env.example).
// These values are safe to expose in client-side bundles — they identify the
// Firebase project and are constrained by Firestore security rules.
const firebaseConfig = {
  apiKey:        import.meta.env['VITE_FIREBASE_API_KEY'] as string,
  authDomain:    import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'] as string,
  projectId:     import.meta.env['VITE_FIREBASE_PROJECT_ID'] as string,
  storageBucket: import.meta.env['VITE_FIREBASE_STORAGE_BUCKET'] as string,
};

// Validate at startup so misconfiguration fails loudly in dev
(['VITE_FIREBASE_API_KEY', 'VITE_FIREBASE_AUTH_DOMAIN', 'VITE_FIREBASE_PROJECT_ID'] as const).forEach(
  (key) => {
    if (!import.meta.env[key]) {
      throw new Error(`[firebase] Missing required env var: ${key}`);
    }
  },
);

// ── Singleton initialisation ────────────────────────────────────────────────
const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);

// Firestore client — used by client-side data service modules (lib/firestore.ts).
// Queries MUST scope by tenantId. Security rules enforce this server-side.
export const db: Firestore = getFirestore(app);

// Firebase Storage — used for study material file uploads.
export const storage: FirebaseStorage = getStorage(app);

// Persist the auth session across page reloads
void setPersistence(auth, browserLocalPersistence);

// ── Google provider ─────────────────────────────────────────────────────────
const googleProvider = new GoogleAuthProvider();
// Request profile and email scopes
googleProvider.addScope('profile');
googleProvider.addScope('email');

// ── Auth helpers ────────────────────────────────────────────────────────────

/** Sign in with Google OAuth popup. */
export async function signInWithGoogle(): Promise<UserCredential> {
  return signInWithPopup(auth, googleProvider);
}

/** Sign in with email and password. */
export async function signInWithEmail(
  email: string,
  password: string,
): Promise<UserCredential> {
  return signInWithEmailAndPassword(auth, email, password);
}

/** Create a new user account with email and password.
 *  NOTE: In the platform model, accounts are typically created by an admin via
 *  POST /admin/users/:uid/claims. Self-registration is only enabled if the tenant
 *  config allows it. */
export async function registerWithEmail(
  email: string,
  password: string,
): Promise<UserCredential> {
  return createUserWithEmailAndPassword(auth, email, password);
}

/** Sign out the current user. */
export async function signOut(): Promise<void> {
  return firebaseSignOut(auth);
}

/**
 * Returns the current user's Firebase ID token (fresh).
 * Force-refreshes the token to ensure custom claims (tenantId, roles) are up-to-date.
 * Use this to attach the Bearer token to API requests via ApiClient.
 */
export async function getIdToken(forceRefresh = false): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('[firebase] No authenticated user — cannot get ID token');
  }
  return user.getIdToken(forceRefresh);
}

/** Send a password reset email to the given address. */
export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

/**
 * Upload a file to Firebase Storage and return its public download URL.
 * @param file - The file to upload.
 * @param path - Storage path, e.g. `tenants/{tenantId}/materials/{subjectId}/{filename}`.
 */
export async function uploadFile(file: File, path: string): Promise<string> {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}
